import { useEffect, useRef } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { FeatureGuideModal } from '@/components/guides/FeatureGuideModal'
import { getFeatureByRoute } from '@/components/guides/guide-content'
import { useAuthStore } from '@/stores/auth'
import { useFeatureGuideStore } from '@/stores/featureGuide'
import { api } from '@/lib/api'

interface LayoutProps {
  children: React.ReactNode
}

// Map routes to spectrum CSS variable names for page-level accent
const ROUTE_ACCENTS: [string, string][] = [
  ['/home', '--spectrum-blue'],
  ['/generate/image/competitor', '--spectrum-teal'],
  ['/generate/image/variation', '--spectrum-cyan'],
  ['/generate/image/freestyle', '--spectrum-blue'],
  ['/generate/image', '--spectrum-blue'],
  ['/generate/video/digital-human', '--spectrum-magenta'],
  ['/generate/video/freestyle', '--spectrum-purple'],
  ['/generate/video', '--spectrum-purple'],
  ['/creative-lab/ideas', '--spectrum-lime'],
  ['/creative-lab/vertical-ideas', '--spectrum-emerald'],
  ['/creative-lab/foreplay', '--spectrum-spring'],
  ['/creative-lab/perplexity', '--spectrum-emerald'],
  ['/creative-lab', '--spectrum-emerald'],
  ['/products', '--spectrum-coral'],
  ['/library', '--spectrum-orange'],
  ['/tasks', '--spectrum-orange'],
  ['/settings/subscription', '--spectrum-coral'],
  ['/settings/billing', '--spectrum-coral'],
  ['/settings', '--spectrum-coral'],
]

function getPageAccentVar(pathname: string): string | null {
  for (const [path, cssVar] of ROUTE_ACCENTS) {
    if (pathname === path || pathname.startsWith(path)) return cssVar
  }
  return null
}

export function Layout({ children }: LayoutProps) {
  const {
    isAuthenticated,
    user,
    allowedPages,
    fetchUser,
    fetchSubscription,
    fetchPageAccess,
  } = useAuthStore()
  const { openGuide, isDismissed } = useFeatureGuideStore()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isEmbed = searchParams.get('embed') === '1'
  const accentVar = getPageAccentVar(location.pathname)
  const prevPathnameRef = useRef(location.pathname)

  // Fetch user's task usage history (once, cached 5min)
  const { data: usedTaskTypes } = useQuery({
    queryKey: ['tasks-usage'],
    queryFn: async () => {
      const res = await api.getTasks({ limit: 50 })
      const types = new Set(res.items.map((t) => t.task_type))
      return types
    },
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated,
  })

  // 页面刷新后恢复用户信息
  useEffect(() => {
    if (isAuthenticated && !user) {
      fetchUser()
      fetchSubscription()
    }
    if (isAuthenticated && allowedPages === null) {
      fetchPageAccess()
    }
  }, [isAuthenticated, user, allowedPages, fetchUser, fetchSubscription, fetchPageAccess])

  // Auto-show guide on first visit to a feature page
  useEffect(() => {
    const pathname = location.pathname
    // Only trigger on route change (not on every render)
    if (pathname === prevPathnameRef.current) return
    prevPathnameRef.current = pathname

    if (!usedTaskTypes) return

    const feature = getFeatureByRoute(pathname)
    if (!feature) return

    // Skip if user has used this feature before (has tasks) or already dismissed guide
    if (usedTaskTypes.has(feature.taskType)) return
    if (isDismissed(feature.featureKey)) return

    // Delay slightly so page renders first
    const timer = setTimeout(() => {
      openGuide(feature.featureKey)
    }, 600)
    return () => clearTimeout(timer)
  }, [location.pathname, usedTaskTypes, isDismissed, openGuide])

  // Also check on first mount (when prevPathnameRef matches current)
  useEffect(() => {
    if (!usedTaskTypes) return

    const feature = getFeatureByRoute(location.pathname)
    if (!feature) return
    if (usedTaskTypes.has(feature.taskType)) return
    if (isDismissed(feature.featureKey)) return

    const timer = setTimeout(() => {
      openGuide(feature.featureKey)
    }, 600)
    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedTaskTypes]) // Only re-run when task data loads

  if (isEmbed) {
    return (
      <div className="flex h-screen overflow-hidden">
        <main className="relative flex-1 overflow-y-auto p-6 min-h-0 scrollbar-thin">
          <div className="relative animate-fade-in">{children}</div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100/50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <Header />
        {/* Accent line under header */}
        <div
          className="h-[2px] shrink-0 transition-all duration-500"
          style={accentVar
            ? { background: `linear-gradient(90deg, hsl(var(${accentVar})) 0%, hsl(var(${accentVar}) / 0.3) 60%, transparent 100%)` }
            : { background: 'transparent' }
          }
        />
        <main className="relative flex-1 overflow-y-auto p-6 min-h-0 scrollbar-thin">
          {/* Subtle accent gradient wash at top of content */}
          {accentVar && (
            <div
              className="absolute inset-x-0 top-0 h-64 pointer-events-none transition-opacity duration-500"
              style={{
                background: `radial-gradient(ellipse 70% 100% at 0% 0%, hsl(var(${accentVar}) / 0.05), transparent 70%)`,
              }}
            />
          )}
          <div className="relative animate-fade-in">{children}</div>
        </main>
      </div>
      <FeatureGuideModal />
    </div>
  )
}
